import getConfig from 'next/config';
import { SLOTS_MAP } from '~/index';
import getCoreServerSideProps from '../modules/core/getCoreServerSideProps';
import jobAdApi from '~/api/server/job-ad';
import recommendationsApi from '~/api/server/recommendations';
import sharedContentApi from '~/api/server/shared-content';
import dynamic from 'next/dynamic';

const CareerPageRenderer = dynamic(
  () =>
    import('~/modules/career-pages/helpers/CareerPageRenderer').then(
      (mod) => mod.CareerPageRenderer,
    ),
  {
    ssr: false,
  },
);
const JobDetail = dynamic(
  () => import('~/modules/career-pages/containers/JobDetail'),
  {
    ssr: false,
  },
);

/**
 * JobDetailSharedContentSlots
 *
 * This constant defines the shared content slots used on the job detail page.
 * These slots are used to dynamically inject shared content into specific sections of the page.
 *
 * @type {Array<string>} An array of slot names corresponding to shared content areas.
 */
export const JobDetailSharedContentSlots = [
  SLOTS_MAP.benefits,
  SLOTS_MAP.currentInformation,
  SLOTS_MAP.recommendation,
  SLOTS_MAP.trust,
  SLOTS_MAP.headerImage,
  SLOTS_MAP.slot1,
  SLOTS_MAP.slot2,
  SLOTS_MAP.slot3,
  SLOTS_MAP.slot4,
  SLOTS_MAP.slot5,
  SLOTS_MAP.slot5,
  SLOTS_MAP.slot5,
  SLOTS_MAP.slot5,
  SLOTS_MAP.information,
  SLOTS_MAP.applyTextBefore,
  SLOTS_MAP.applyTextAfter,
];

/**
 * Fetches server-side props for the job detail page.
 *
 * This function retrieves data required to render the job detail page, including:
 * - Job advertisement details
 * - Recommendations
 * - Shared content slots
 * - Structured data for SEO
 *
 * @param {Object} ctx - The Next.js context object.
 * @param {Object} ctx.query - Query parameters from the URL.
 * @param {string} ctx.locale - The current locale of the page.
 * @returns {Promise<Object>} A promise that resolves to the props for the page.
 * @property {Object} props.pageContent - Content data for the page, including job details and analytics data.
 * @property {Object} props.siteContent - General site content data.
 * @property {Object} props.context - Contextual information, such as locale and query parameters.
 * @property {Object} props.jobAdResponse - API response containing job advertisement details.
 * @property {Object} props.schemaResponse - API response containing structured data (JSON-LD schema).
 * @property {Object} props.recommendationsResponse - API response containing job recommendations.
 * @property {Object} props.sharedContentResponse - API response containing shared content for the page.
 */
export const getServerSideProps = async (ctx) => {
  const { publicRuntimeConfig } = getConfig();
  const { pageContent, siteContent, context } = getCoreServerSideProps(ctx);

  const targetLanguage = context?.query?.target;
  const language = context.locale || publicRuntimeConfig.defaultLanguage;
  // Get Job Ad schema
  const schemaResponse = await jobAdApi.get(pageContent.jobAdId, {
    format: 'SCHEMA_ORG',
    language,
  });

  // Get Job Ad details
  const jobAdResponse = await jobAdApi.get(pageContent.jobAdId, {
    format: 'CONTENT',
    language,
    targetLanguage,
  });

  // Get Recommendations
  const recommendationsResponse = await recommendationsApi.get(
    pageContent.jobAdId,
    {
      language,
      type: 'JOB_AD',
    },
  );

  // Get Shared Content
  const sharedContentResponse = await sharedContentApi.get(
    pageContent.jobAdId,
    {
      language,
      type: 'JOB_AD',
      slots: JobDetailSharedContentSlots,
    },
  );

  return {
    props: {
      pageContent,
      siteContent,
      context,
      jobAdResponse,
      schemaResponse,
      recommendationsResponse,
      sharedContentResponse,
    },
  };
};

/**
 * JobDetailNextPage Component
 *
 * This component renders the job detail page, including:
 * - Job advertisement details
 * - Recommendations
 * - Shared content slots
 * - Structured data for SEO
 * - DataLayer integration for analytics
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.pageContent - Content data for the page, including job details and analytics data.
 * @param {Object} props.siteContent - General site content data.
 * @param {Object} props.context - Contextual information, such as locale and query parameters.
 * @param {Object} props.jobAdResponse - API response containing job advertisement details.
 * @param {Object} props.schemaResponse - API response containing structured data (JSON-LD schema).
 * @param {Object} props.recommendationsResponse - API response containing job recommendations.
 * @param {Object} props.sharedContentResponse - API response containing shared content for the page.
 * @returns {React.ReactElement | null} The rendered job detail page.
 */
function JobDetailNextPage(props) {
  const language = props.context.locale;

  const locationFound = props.jobAdResponse?.data?.jobAd?.attributes.find(
    (el) => el.title === 'Standort' || el.title === 'Location',
  );
  const location =
    locationFound?.value &&
    locationFound?.value.length > 0 &&
    locationFound?.value[0].length > 0
      ? locationFound.value[0][0]
      : '';

  return (
    <>
      <CareerPageRenderer
        {...props}
        withBreadcrumb={true}
        withFootShare={false}
      >
        <JobDetail
          jobAd={props.jobAdResponse?.data?.jobAd}
          bookmarkId={props.pageContent?.bookmarkId}
          schema={props.schemaResponse?.data?.jobAd}
          recommendations={props.recommendationsResponse?.data}
          language={language}
          sharedContent={props.sharedContentResponse?.data}
          jobAdId={props.pageContent?.jobAdId}
          location={location}
        />
      </CareerPageRenderer>
    </>
  );
}

export default JobDetailNextPage;
